import React, { useState, useEffect } from "react";
import Moment from "react-moment";
import { FaRegEdit, FaGift, FaFolder } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { Services } from "Services";
import { SuccessSwal, warningSwal } from "Util/Toast";
import SkeletonLoad from "Components/TableLoad/load";
import CommonClasses from "Styles/Common.module.css";
import { IoIosWarning } from "react-icons/io";
import { AiFillDelete } from "react-icons/ai";
import { Button, Modal, Box, Typography, Paper, TablePagination } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

function Tutor() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [assignGiftsModal, setAssignGiftsModal] = useState(false);
    const [availableGifts, setAvailableGifts] = useState([]);
    const [selectedGifts, setSelectedGifts] = useState([]);
    const [giftsLoading, setGiftsLoading] = useState(false);
    const [expandedImages, setExpandedImages] = useState({});
    const [imageErrors, setImageErrors] = useState({});
    const token = { Authorization: `token ${localStorage.getItem("token")}` };

    const fetchCategories = () => {
        setLoading(true);
        Services.getCategories("POST", null, token)
            .then((response) => {
                setLoading(false);
                if (response.Status === 1) {
                    setData(response.Categories);
                } else if (response.Status === 0) {
                    warningSwal("Warning", response.Message);
                }
            })
            .catch((err) => {
                setLoading(false);
                alert("Something went wrong. Please try again.");
                console.error(err);
            });
    };

    useEffect(() => {
        fetchCategories();
    }, []);

    const handleDeleteClick = (category) => {
        setSelectedCategory(category);
        setShowModal(true);
    };

    const confirmDelete = () => {
        if (selectedCategory) {
            Services.deleteCategory(selectedCategory.CategoryID, token)
                .then((response) => {
                    if (response.Status === 1) {
                        SuccessSwal("Success", "Category deleted successfully!");
                        fetchCategories(); // Refresh the categories list
                    } else {
                        warningSwal("Warning", response.Message);
                    }
                })
                .catch((err) => {
                    alert("Something went wrong. Please try again.");
                    console.error(err);
                })
                .finally(() => {
                    setShowModal(false);
                    setSelectedCategory(null);
                });
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleAssignGiftsClick = (category) => {
        setSelectedCategory(category);
        setAssignGiftsModal(true);
        fetchAvailableGifts(category.CategoryID);
    };

    const fetchAvailableGifts = (categoryId) => {
        setGiftsLoading(true);
        Services.listGiftsNotInCategory(categoryId, token)
            .then((response) => {
                setGiftsLoading(false);
                if (response.Status === 1) {
                    // Transform the data to match DataGrid format with more fields
                    const transformedGifts = response.Gifts.map((gift, index) => ({
                        id: gift.GiftID,
                        giftId: gift.GiftID,
                        giftName: gift.GiftName,
                        giftDescription: gift.GiftDescription,
                        giftImages: gift.GiftImages || [],
                        price: gift.Price,
                        offerPrice: gift.OfferPrice,
                        material: gift.Material,
                        capacity: gift.Capacity,
                        productDimension: gift.ProductDimension,
                        topDiameter: gift.TopDiameter,
                        packageInclude: gift.PackageInclude,
                        quantityAvailable: gift.QuantityAvailable,
                        isTrending: gift.IsTrending === 1 ? 'Yes' : 'No',
                        isReturnable: gift.IsReturnable === 1 ? 'Yes' : 'No',
                        isCODAvailable: gift.IsCODAvailable === 1 ? 'Yes' : 'No',
                        isBulkUploaded: gift.IsBulkUploaded === 1 ? 'Yes' : 'No',
                        giftStatus: gift.GiftStatus === 1 ? 'Active' : 'Inactive',
                        giftRating: gift.GiftRating || 0,
                        ratingCount: gift.RatingCount || 0,
                        productId: gift.ProductID,
                        vendorId: gift.VendorID,
                        createdAt: gift.CreatedAt,
                        updatedAt: gift.UpdateAt
                    }));
                    setAvailableGifts(transformedGifts);
                } else {
                    warningSwal("Warning", response.Message);
                }
            })
            .catch((err) => {
                setGiftsLoading(false);
                alert("Something went wrong. Please try again.");
                console.error(err);
            });
    };

    const handleAssignGifts = () => {
        if (selectedGifts.length === 0) {
            warningSwal("Warning", "Please select at least one gift to assign.");
            return;
        }

        const giftIds = selectedGifts.map(giftId => parseInt(giftId));
        const requestBody = { GiftIDs: giftIds };

        Services.assignCategoryToGifts(selectedCategory.CategoryID, "PUT", JSON.stringify(requestBody), token)
            .then((response) => {
                if (response.Status === 1) {
                    SuccessSwal("Success", "Gifts assigned to category successfully!");
                    setAssignGiftsModal(false);
                    setSelectedGifts([]);
                    setSelectedCategory(null);
                } else {
                    warningSwal("Warning", response.Message);
                }
            })
            .catch((err) => {
                alert("Something went wrong. Please try again.");
                console.error(err);
            });
    };

    const handleCloseAssignModal = () => {
        setAssignGiftsModal(false);
        setSelectedGifts([]);
        setSelectedCategory(null);
        setAvailableGifts([]);
        setExpandedImages({});
    };

    const toggleImageExpansion = (giftId) => {
        setExpandedImages(prev => ({
            ...prev,
            [giftId]: !prev[giftId]
        }));
    };

    const handleImageError = (categoryId) => {
        setImageErrors(prev => ({
            ...prev,
            [categoryId]: true
        }));
    };

    return (
        <>
            {loading ? (
                <SkeletonLoad
                    TableHead={[
                        "Category Image",
                        "Name",
                        "Created",
                        "Status",
                        "Actions",
                    ]}
                />
            ) : data.length > 0 ? (
                <div className={CommonClasses["Table"]}>
                    <table>
                        <thead>
                            <tr>
                                <th>Category Image</th>
                                <th>Name</th>
                                <th>Created </th>
                                <th>Status</th>
                                <th>Assign Gifts</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data
                                .slice(
                                    page * rowsPerPage,
                                    page * rowsPerPage + rowsPerPage
                                )
                                .map((item) => (
                                    <tr
                                        key={item.CategoryID}
                                        id={item.CategoryID}
                                        name={item.CategoryName}
                                    >
                                        <td style={{ padding: '8px', textAlign: 'center', verticalAlign: 'middle' }}>
                                            {(!item.CategoryImage || imageErrors[item.CategoryID]) ? (
                                                <div
                                                    style={{
                                                        width: "6rem",
                                                        height: "5rem",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        backgroundColor: "#f0f0f0",
                                                        borderRadius: "5px",
                                                        border: "2px solid #ddd",
                                                        margin: "0 auto"
                                                    }}
                                                >
                                                    <FaFolder size={40} color="#999" />
                                                </div>
                                            ) : (
                                                <img
                                                    style={{
                                                        width: "6rem",
                                                        height: "5rem",
                                                        objectFit: "cover",
                                                        borderRadius: "5px",
                                                        display: "block",
                                                        margin: "0 auto"
                                                    }}
                                                    src={item.CategoryImage}
                                                    alt="category-img"
                                                    onError={() => handleImageError(item.CategoryID)}
                                                />
                                            )}
                                        </td>
                                        <td>{item.CategoryName}</td>

                                        <td>
                                            <Moment format="DD-MM-YYYY">
                                                {item.CreatedAt}
                                            </Moment>{" "}
                                        </td>
                                        <td>
                                            {item.CategoryStatus === 1 ? (
                                                <div
                                                    className={
                                                        CommonClasses[
                                                            "Status-active"
                                                        ]
                                                    }
                                                >
                                                    <span>Active</span>
                                                </div>
                                            ) : (
                                                <div
                                                    className={
                                                        CommonClasses[
                                                            "Status-inactive"
                                                        ]
                                                    }
                                                >
                                                    <span>Inactive</span>
                                                </div>
                                            )}
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <button
                                                onClick={() => handleAssignGiftsClick(item)}
                                                style={{
                                                    backgroundColor: '#4CAF50',
                                                    color: 'white',
                                                    border: 'none',
                                                    padding: '8px 12px',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '5px',
                                                    fontSize: '12px',
                                                    margin: '0 auto'
                                                }}
                                                title="Assign Gifts to Category"
                                            >
                                                <FaGift />
                                                Assign Gifts
                                            </button>
                                        </td>
                                        <td>
                                            <div
                                                className={
                                                    CommonClasses["Action-btns"]
                                                }
                                            >
                                                <button
                                                    onClick={() =>
                                                        navigate(
                                                            `/updateCategory/${item.CategoryID}`,
                                                            {
                                                                state: {
                                                                    category: item,
                                                                },
                                                            }
                                                        )
                                                    }
                                                >
                                                    <FaRegEdit />
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteClick(
                                                            item
                                                        );
                                                    }}
                                                >
                                                    <AiFillDelete
                                                        className={
                                                            CommonClasses[
                                                                "del-main-btn"
                                                            ]
                                                        }
                                                    />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={data.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        className={CommonClasses["pagination"]}
                    />
                </div>
            ) : (
                <div>
                    <p>No Categories Found</p>
                </div>
            )}
            {showModal && (
                <div className={CommonClasses["modal"]}>
                    <div
                        className={CommonClasses["modal-content"]}
                        data-aos="zoom-in"
                    >
                        <h1>
                            <IoIosWarning
                                className={CommonClasses["del-warning"]}
                            />
                        </h1>
                        <h3>Confirm Delete</h3>
                        <p>Are you sure you want to delete this Category?</p>
                        <div className={CommonClasses["modal-actions"]}>
                            <Button variant="outlined" onClick={confirmDelete}>
                                OK
                            </Button>
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={() => setShowModal(false)}
                            >
                                Cancel
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Assign Gifts Modal */}
            <Modal
                open={assignGiftsModal}
                onClose={handleCloseAssignModal}
                aria-labelledby="assign-gifts-modal"
                aria-describedby="assign-gifts-description"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '95%',
                        maxWidth: 1200,
                        maxHeight: '95%',
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        boxShadow: 24,
                        p: 4,
                        overflow: 'auto'
                    }}
                >
                    <Typography id="assign-gifts-modal" variant="h5" component="h2" sx={{ mb: 2, fontWeight: 'bold', color: '#FF0D0D' }}>
                        Assign Gifts to Category: {selectedCategory?.CategoryName}
                    </Typography>
                    
                    <Typography id="assign-gifts-description" sx={{ mb: 3, color: 'text.secondary' }}>
                        Select the gifts you want to assign to this category. Only gifts not already assigned to this category are shown.
                        Total available gifts: {availableGifts.length}
                    </Typography>

                    {giftsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <Typography>Loading available gifts...</Typography>
                        </Box>
                    ) : (
                        <Paper sx={{ height: 400, width: '100%', mb: 3 }}>
                            <DataGrid
                                rows={availableGifts}
                                columns={[
                                    { 
                                        field: 'giftImages', 
                                        headerName: 'Images', 
                                        width: 180,
                                        renderCell: (params) => {
                                            const isExpanded = expandedImages[params.row.giftId];
                                            const images = params.value || [];
                                            const displayImages = isExpanded ? images : images.slice(0, 2);
                                            
                                            return (
                                                <Box sx={{ 
                                                    display: 'flex', 
                                                    flexDirection: 'column',
                                                    gap: 0.5,
                                                    alignItems: 'center',
                                                    p: 1,
                                                    width: '100%'
                                                }}>
                                                    <Box sx={{ 
                                                        display: 'flex', 
                                                        gap: 0.5, 
                                                        flexWrap: isExpanded ? 'nowrap' : 'wrap',
                                                        justifyContent: isExpanded ? 'flex-start' : 'center',
                                                        overflowX: isExpanded ? 'auto' : 'visible',
                                                        width: '100%',
                                                        pb: 0.5
                                                    }}>
                                                        {displayImages.map((image, index) => (
                                                            <img
                                                                key={index}
                                                                src={image}
                                                                alt={`Gift ${index + 1}`}
                                                                style={{
                                                                    width: '35px',
                                                                    height: '35px',
                                                                    objectFit: 'cover',
                                                                    borderRadius: '6px',
                                                                    border: '2px solid #e0e0e0',
                                                                    cursor: 'pointer',
                                                                    transition: 'transform 0.2s ease'
                                                                }}
                                                                onClick={() => window.open(image, '_blank')}
                                                                onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
                                                                onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                                                            />
                                                        ))}
                                                    </Box>
                                                    {images.length > 2 && (
                                                        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                                                            <Box 
                                                                sx={{ 
                                                                    mt: 0.5,
                                                                    minWidth: '70px',
                                                                    height: '28px', 
                                                                    display: 'flex', 
                                                                    alignItems: 'center', 
                                                                    justifyContent: 'center',
                                                                    backgroundColor: isExpanded ? '#4CAF50' : '#f5f5f5',
                                                                    color: isExpanded ? 'white' : '#666',
                                                                    borderRadius: '6px',
                                                                    fontSize: '12px',
                                                                    fontWeight: 'bold',
                                                                    cursor: 'pointer',
                                                                    transition: 'all 0.2s ease',
                                                                    '&:hover': {
                                                                        backgroundColor: isExpanded ? '#45a049' : '#e0e0e0'
                                                                    }
                                                                }}
                                                                onClick={() => toggleImageExpansion(params.row.giftId)}
                                                            >
                                                                {isExpanded ? 'Hide' : `+${images.length - 2}`}
                                                            </Box>
                                                        </Box>
                                                    )}
                                                </Box>
                                            );
                                        },
                                        sortable: false,
                                        filterable: false
                                    },
                                    { field: 'giftId', headerName: 'ID', width: 70 },
                                    { 
                                        field: 'giftName', 
                                        headerName: 'Gift Name', 
                                        width: 250,
                                        renderCell: (params) => (
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                                    {params.value}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ 
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}>
                                                    {params.row.giftDescription}
                                                </Typography>
                                            </Box>
                                        )
                                    },
                                    { field: 'productId', headerName: 'Product ID', width: 100 },
                                    { field: 'vendorId', headerName: 'Vendor ID', width: 100 },
                                    { 
                                        field: 'price', 
                                        headerName: 'Price', 
                                        width: 90, 
                                        type: 'number',
                                        renderCell: (params) => `₹${params.value}`
                                    },
                                    { 
                                        field: 'offerPrice', 
                                        headerName: 'Offer Price', 
                                        width: 110, 
                                        type: 'number',
                                        renderCell: (params) => (
                                            <Typography sx={{ color: '#FF0D0D', fontWeight: 'bold' }}>
                                                ₹{params.value}
                                            </Typography>
                                        )
                                    },
                                    { field: 'material', headerName: 'Material', width: 120 },
                                    { field: 'capacity', headerName: 'Capacity', width: 120 },
                                    { 
                                        field: 'quantityAvailable', 
                                        headerName: 'Qty Available', 
                                        width: 120, 
                                        type: 'number',
                                        renderCell: (params) => (
                                            <Typography sx={{ 
                                                color: params.value > 0 ? 'green' : 'red',
                                                fontWeight: 'bold',
                                                fontSize: '14px'
                                            }}>
                                                {params.value}
                                            </Typography>
                                        )
                                    },
                                    {
                                        field: 'isTrending',
                                        headerName: 'Trending',
                                        width: 100,
                                        renderCell: (params) => (
                                            <Typography sx={{
                                                fontWeight: 'bold',
                                                color: params.value === 'Yes' ? '#2e7d32' : '#666'
                                            }}>
                                                {params.value}
                                            </Typography>
                                        )
                                    },
                                    {
                                        field: 'giftStatus',
                                        headerName: 'Status',
                                        width: 90,
                                        renderCell: (params) => (
                                            <Typography sx={{
                                                fontWeight: 'bold',
                                                color: params.value === 'Active' ? '#2e7d32' : '#d32f2f'
                                            }}>
                                                {params.value}
                                            </Typography>
                                        )
                                    },
                                    {
                                        field: 'isReturnable',
                                        headerName: 'Returnable',
                                        width: 110,
                                        renderCell: (params) => (
                                            <Typography sx={{
                                                fontWeight: 'bold',
                                                color: params.value === 'Yes' ? '#2e7d32' : '#666'
                                            }}>
                                                {params.value}
                                            </Typography>
                                        )
                                    },
                                    {
                                        field: 'isCODAvailable',
                                        headerName: 'COD',
                                        width: 80,
                                        renderCell: (params) => (
                                            <Typography sx={{
                                                fontWeight: 'bold',
                                                color: params.value === 'Yes' ? '#2e7d32' : '#666'
                                            }}>
                                                {params.value}
                                            </Typography>
                                        )
                                    }
                                ]}
                                initialState={{ 
                                    pagination: { paginationModel: { page: 0, pageSize: 15 } } 
                                }}
                                pageSizeOptions={[10, 15, 25, 50]}
                                checkboxSelection
                                disableRowSelectionOnClick
                                onRowSelectionModelChange={(newSelection) => {
                                    setSelectedGifts(newSelection);
                                }}
                                rowSelectionModel={selectedGifts}
                                sx={{ 
                                    border: 0,
                                    '& .MuiDataGrid-cell': {
                                        borderBottom: '1px solid #f0f0f0',
                                        padding: '8px 4px',
                                        display: 'flex',
                                        alignItems: 'center'
                                    },
                                    '& .MuiDataGrid-columnHeaders': {
                                        backgroundColor: '#f8f9fa',
                                        fontWeight: 'bold',
                                        fontSize: '13px',
                                        padding: '12px 4px'
                                    },
                                    '& .MuiDataGrid-columnHeaderTitle': {
                                        fontWeight: 'bold',
                                        fontSize: '13px'
                                    },
                                    '& .MuiDataGrid-row': {
                                        '&:hover': {
                                            backgroundColor: '#f5f5f5'
                                        }
                                    },
                                    '& .MuiDataGrid-cell:focus': {
                                        outline: 'none'
                                    },
                                    '& .MuiDataGrid-cell:focus-within': {
                                        outline: 'none'
                                    }
                                }}
                                getRowHeight={() => 60}
                            />
                        </Paper>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            Selected: {selectedGifts.length} gift{selectedGifts.length !== 1 ? 's' : ''}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button 
                                variant="outlined" 
                                onClick={handleCloseAssignModal}
                            >
                                Cancel
                            </Button>
                            <Button 
                                variant="contained" 
                                onClick={handleAssignGifts}
                                disabled={selectedGifts.length === 0 || giftsLoading}
                                sx={{ backgroundColor: '#4CAF50', '&:hover': { backgroundColor: '#45a049' } }}
                            >
                                Assign Selected Gifts ({selectedGifts.length})
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Modal>
        </>
    );
}

export default Tutor;
